import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Chip from '@mui/material/Chip/Chip';
import Alert from '@mui/material/Alert/Alert';
import IconButton from '@mui/material/IconButton/IconButton';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import TextField from '@mui/material/TextField/TextField';
import Snackbar from '@mui/material/Snackbar/Snackbar';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SendIcon from '@mui/icons-material/Send';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { PageContainer } from '../../components/PageContainer';
import { Loading } from '../../components/Loading';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { SessionDialog } from '../../components/Sessions/SessionDialog';
import { useHackathon } from '../../hooks/useHackathon';
import { useSessions } from '../../hooks/useSessions';
import { useAuth } from '../../services/useAuth';
import { autoAssignSessions, deleteSession, notifySessionAssignments, SessionPayload } from '../../services/session.service';
import { categoryLabels, sessionWarnings } from '../../utils/sessions';

const PeopleRow = ({ label, people }: { label: string, people: Array<{ _id: string, username: string }> }) => (
    <Stack spacing={0.5}>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        {
            people.length === 0
                ? <Typography variant="body2" color="text.secondary">None yet</Typography>
                : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {people.map(p => <Chip key={p._id} size="small" label={p.username} />)}
                    </Stack>
                )
        }
    </Stack>
)

export const ManageSessions = () => {
    const { id } = useParams();
    const { hackathon, loading } = useHackathon(id);
    const { sessions, loading: sessionsLoading } = useSessions(id);
    const { user } = useAuth();

    const [editing, setEditing] = useState<SessionPayload | 'new' | null>(null);
    const [deleting, setDeleting] = useState<SessionPayload | null>(null);
    const [autoAssignOpen, setAutoAssignOpen] = useState(false);
    const [minJudges, setMinJudges] = useState(2);
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const isOrganizer = !!user && !!hackathon && (user.isAdmin || hackathon.organizers.some(o => o.email === user.email));
    const warnings = useMemo(() => hackathon ? sessionWarnings(hackathon, sessions) : [], [hackathon, sessions]);

    if (!hackathon && loading) {
        return <Loading />
    }
    if (!hackathon || !id) {
        return <PageContainer><Alert severity="error">Hackathon not found</Alert></PageContainer>
    }
    if (!isOrganizer) {
        return <PageContainer><Alert severity="error">Only organizers can manage sessions.</Alert></PageContainer>
    }

    const run = (action: () => Promise<unknown>, success: string) => {
        setBusy(true);
        setError('');
        action().then(() => setMessage(success), err => {
            setError(err.response?.data?.message || 'Something went wrong');
        }).finally(() => setBusy(false));
    };

    const ideaTitle = (ideaId: string) => hackathon.ideas.find(i => i._id === ideaId)?.title || 'Removed team';

    return (
        <PageContainer variant="plain">
            <Stack spacing={3}>
                <AppBreadcrumbs crumbs={[
                    { type: PageType.Home, name: 'Home', link: '/' },
                    { type: PageType.Hackathon, name: hackathon.name, link: `/h/${hackathon.slug}` },
                    { type: PageType.Organizer, name: 'Sessions' }
                ]} />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-end' }}>
                    <div>
                        <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>Sessions</Typography>
                        <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
                            Split judging into rooms that run at the same time. Each session judges some of the categories, with its own judges, hosts and teams.
                        </Typography>
                    </div>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>New session</Button>
                        <Button variant="outlined" startIcon={<ShuffleIcon />} disabled={sessions.length === 0 || busy} onClick={() => setAutoAssignOpen(true)}>Auto-assign</Button>
                        <Button variant="outlined" startIcon={<SendIcon />} disabled={sessions.length === 0 || busy} onClick={() => setNotifyOpen(true)}>Send assignments</Button>
                    </Stack>
                </Stack>

                {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

                {
                    warnings.length > 0 && (
                        <Alert severity={warnings.some(w => w.severity === 'warning') ? 'warning' : 'info'}>
                            <Typography variant="subtitle2" sx={{ mb: warnings.length > 1 ? 0.5 : 0 }}>
                                {warnings.length === 1 ? warnings[0].message : `${warnings.length} things to check`}
                            </Typography>
                            {
                                warnings.length > 1 && (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {warnings.map((w, i) => <li key={i}><Typography variant="body2">{w.message}</Typography></li>)}
                                    </Box>
                                )
                            }
                        </Alert>
                    )
                }

                {sessionsLoading && sessions.length === 0 && <Loading />}

                {
                    !sessionsLoading && sessions.length === 0 && (
                        <Card sx={{ borderStyle: 'dashed' }}>
                            <CardContent sx={{ py: 6, textAlign: 'center' }}>
                                <Stack spacing={2} alignItems="center">
                                    <MeetingRoomIcon color="primary" sx={{ fontSize: 44 }} />
                                    <Typography variant="h3" component="h2">No sessions yet</Typography>
                                    <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
                                        Every hackathon has at least one session. Reload the page, or create one to get started.
                                    </Typography>
                                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>Create the first session</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    )
                }

                <Box sx={{
                    display: 'grid',
                    gap: 2.5,
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
                }}>
                    {
                        sessions.map(session => (
                            <Card key={session._id}>
                                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="h4" component="h2">
                                                    <Link to={`/h/${hackathon.slug}/s/${session.slug}`} style={{ color: 'inherit' }}>{session.name}</Link>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">/s/{session.slug}</Typography>
                                            </Box>
                                            <Stack direction="row">
                                                <Tooltip title="Edit">
                                                    <IconButton aria-label={`Edit ${session.name}`} onClick={() => setEditing(session)}><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title={sessions.length <= 1 ? 'A hackathon needs at least one session' : 'Delete'}>
                                                    <span>
                                                        <IconButton aria-label={`Delete ${session.name}`} disabled={sessions.length <= 1} onClick={() => setDeleting(session)}><DeleteOutlineIcon /></IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>

                                        <Stack spacing={0.5}>
                                            <Typography variant="overline" color="text.secondary">Categories{session.allCategories ? ' · all, automatically' : ''}</Typography>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {categoryLabels(hackathon, session.categories).map(label => <Chip key={label} size="small" color="primary" variant="outlined" label={label} />)}
                                                {session.categories.length === 0 && <Typography variant="body2" color="text.secondary">None yet</Typography>}
                                            </Stack>
                                        </Stack>

                                        <PeopleRow label={session.allJudges ? 'Judges · all, automatically' : 'Judges'} people={session.judges} />
                                        <PeopleRow label="Hosts" people={session.hosts} />

                                        <Stack spacing={0.5}>
                                            <Typography variant="overline" color="text.secondary">Teams ({session.ideas.length}){session.allIdeas ? ' · all, automatically' : ''}</Typography>
                                            {
                                                session.ideas.length === 0
                                                    ? <Typography variant="body2" color="text.secondary">None yet</Typography>
                                                    : (
                                                        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                                            {session.ideas.map(ideaId => (
                                                                <li key={ideaId}><Typography variant="body2">{ideaTitle(ideaId)}</Typography></li>
                                                            ))}
                                                        </Box>
                                                    )
                                            }
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))
                    }
                </Box>
            </Stack>

            {
                editing && (
                    <SessionDialog
                        open
                        hackathonId={id}
                        hackathon={hackathon}
                        session={editing === 'new' ? undefined : editing}
                        onClose={() => setEditing(null)}
                        onSaved={(saved, notified) => {
                            setEditing(null);
                            setMessage(notified > 0 ? `${saved.name} saved, ${notified} people notified` : `${saved.name} saved`);
                        }} />
                )
            }

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle>Delete {deleting?.name}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Its link will stop working. Teams, judges and votes are not deleted.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(null)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={() => {
                        const target = deleting!;
                        setDeleting(null);
                        run(() => deleteSession(id, target._id), `${target.name} deleted`);
                    }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={autoAssignOpen} onClose={() => setAutoAssignOpen(false)}>
                <DialogTitle>Auto-assign teams and judges</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <DialogContentText>
                            Replaces the teams and judges in every session with an even spread. Each team presents in one session,
                            and judges are kept out of sessions their own team presents in. Categories, hosts and descriptions stay as they are.
                        </DialogContentText>
                        <TextField
                            type="number"
                            label="Minimum judges per session"
                            value={minJudges}
                            inputProps={{ min: 0, max: hackathon.judges.length }}
                            helperText={`${hackathon.judges.length} judge${hackathon.judges.length === 1 ? '' : 's'} in this hackathon. Judges may cover more than one session to reach this.`}
                            onChange={e => setMinJudges(Math.max(0, Number(e.target.value) || 0))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAutoAssignOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={() => {
                        setAutoAssignOpen(false);
                        run(() => autoAssignSessions(id, minJudges), 'Teams and judges assigned');
                    }}>Assign</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={notifyOpen} onClose={() => setNotifyOpen(false)}>
                <DialogTitle>Send session assignments?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Everyone judging, hosting or presenting gets a notification listing their sessions, with a link to each.
                        Send this once assignments are final; sending again sends everyone a new notification.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotifyOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={() => {
                        setNotifyOpen(false);
                        setBusy(true);
                        notifySessionAssignments(id).then(sent => {
                            setMessage(`Sent to ${sent} ${sent === 1 ? 'person' : 'people'}`);
                        }, err => setError(err.response?.data?.message || 'Could not send')).finally(() => setBusy(false));
                    }}>Send</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!message} autoHideDuration={4000} onClose={() => setMessage('')} message={message} />
        </PageContainer>
    )
}
